import { useState } from 'react';
import {
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useRunBatchEvaluation } from '@/features/evaluation/hooks/useEvaluation';
import { useScreenSources, estimateScreeningCredits } from '@/features/screening/hooks/useScreenSources';
import { useHasCredits } from '@/features/dashboard/hooks/useCredits';
import type { AIModel, BatchEvaluationProgress, EvaluationMode, ScreeningProgress } from '@/features/evaluation/types';
import { AI_MODEL_LABELS, SCREENING_BATCH_SIZE } from '@/features/evaluation/types';
import { toast } from 'sonner';

interface EvalRunnerProps {
  sourceIds: string[];
  criteriaIds: string[];
  evaluationMode: EvaluationMode;
  onComplete?: (progress: BatchEvaluationProgress) => void;
}

export function EvalRunner({
  sourceIds,
  criteriaIds,
  evaluationMode,
  onComplete,
}: EvalRunnerProps) {
  const [aiModel, setAiModel] = useState<AIModel>('gpt-4-turbo');
  const [progress, setProgress] = useState<BatchEvaluationProgress | null>(null);
  const [screeningProgress, setScreeningProgress] = useState<ScreeningProgress | null>(null);

  const batchEvaluation = useRunBatchEvaluation();
  const screenSources = useScreenSources();

  const isScreening = evaluationMode === 'screening';
  const creditsNeeded = isScreening
    ? estimateScreeningCredits(sourceIds.length)
    : sourceIds.length;

  const { hasCredits, credits, isLoading: creditsLoading } = useHasCredits(creditsNeeded);

  const isRunning = batchEvaluation.isPending || screenSources.isPending;
  const isReady = sourceIds.length > 0 && criteriaIds.length > 0 && !isRunning;

  // Progress calculations
  const fullProgressPercent = progress
    ? ((progress.completed + progress.failed) / progress.total) * 100
    : 0;
  const screeningProgressPercent = screeningProgress
    ? (screeningProgress.completed_sources / screeningProgress.total_sources) * 100
    : 0;
  async function handleRun() {
    if (!isReady) return;

    if (!hasCredits) {
      toast.error(
        `Not enough credits. You need ${creditsNeeded} but have ${credits}.`
      );
      return;
    }

    if (isScreening) {
      try {
        await screenSources.mutateAsync({
          source_ids: sourceIds,
          criteria_ids: criteriaIds,
          onProgress: setScreeningProgress,
        });
        // Switch to history tab via onComplete
        onComplete?.({
          total: sourceIds.length,
          completed: sourceIds.length,
          failed: 0,
          current_source_id: null,
          results: [],
        });
      } catch {
        toast.error('Screening failed');
      }
    } else {
      try {
        const result = await batchEvaluation.mutateAsync({
          source_ids: sourceIds,
          criteria_ids: criteriaIds,
          ai_model: aiModel,
          onProgress: setProgress,
        });
        onComplete?.(result);
        toast.success(
          `Evaluation complete: ${result.completed} succeeded, ${result.failed} failed`
        );
      } catch {
        toast.error('Batch evaluation failed');
      }
    }
  }

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-900">
            {isScreening ? 'Run Screening' : 'Run Evaluation'}
          </h3>
          <p className="text-xs text-slate-500">
            {sourceIds.length} source{sourceIds.length !== 1 ? 's' : ''} ×{' '}
            {criteriaIds.length} criteri{criteriaIds.length !== 1 ? 'a' : 'on'}
          </p>
        </div>
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            !creditsLoading && !hasCredits && 'border-rose-300 text-rose-600'
          )}
        >
          <Zap className="mr-1 h-3 w-3" />
          {creditsLoading ? '...' : `${credits} credits`}
        </Badge>
      </div>

      {/* Screening info banner */}
      {isScreening && !isRunning && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs font-medium text-emerald-800">
            Screening mode — GPT-4o-mini
          </p>
          <p className="mt-0.5 text-xs text-emerald-600">
            {Math.ceil(sourceIds.length / SCREENING_BATCH_SIZE)} batch
            {Math.ceil(sourceIds.length / SCREENING_BATCH_SIZE) !== 1 ? 'es' : ''} of {SCREENING_BATCH_SIZE} — title &amp; abstract only.
            ~$0.002 per batch.
          </p>
        </div>
      )}

      {/* AI model selector (full mode only) */}
      {!isScreening && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">AI Model:</label>
          <Select
            value={aiModel}
            onValueChange={(v) => setAiModel(v as AIModel)}
            disabled={isRunning}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(AI_MODEL_LABELS) as [AIModel, string][]).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Readiness checks */}
      {!isRunning && (
        <div className="space-y-1">
          <ReadinessCheck
            label="Sources selected"
            ok={sourceIds.length > 0}
            detail={`${sourceIds.length} selected`}
          />
          <ReadinessCheck
            label="Criteria selected"
            ok={criteriaIds.length > 0}
            detail={`${criteriaIds.length} selected`}
          />
          <ReadinessCheck
            label="Credits available"
            ok={hasCredits}
            detail={
              hasCredits
                ? `${creditsNeeded} needed, ${credits} available`
                : `Need ${creditsNeeded}, have ${credits}`
            }
          />
        </div>
      )}

      {/* Progress — Full mode */}
      {isRunning && !isScreening && progress && (
        <div className="space-y-3">
          <Progress value={fullProgressPercent} className="h-2" />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              {progress.completed + progress.failed} / {progress.total}
            </span>
            <div className="flex gap-3">
              {progress.completed > 0 && (
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle className="h-3 w-3" />
                  {progress.completed}
                </span>
              )}
              {progress.failed > 0 && (
                <span className="flex items-center gap-1 text-rose-600">
                  <XCircle className="h-3 w-3" />
                  {progress.failed}
                </span>
              )}
            </div>
          </div>
          {progress.current_source_id && (
            <p className="text-xs text-slate-400">
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
              Evaluating source...
            </p>
          )}
        </div>
      )}

      {/* Progress — Screening mode */}
      {isRunning && isScreening && screeningProgress && (
        <div className="space-y-3">
          <Progress value={screeningProgressPercent} className="h-2" />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              Batch {screeningProgress.completed_batches + screeningProgress.failed_batches} / {screeningProgress.total_batches}
            </span>
            <span>
              {screeningProgress.completed_sources} / {screeningProgress.total_sources} sources
            </span>
          </div>
          <p className="text-xs text-slate-400">
            <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
            Screening batch...
          </p>
        </div>
      )}

      {/* Run button */}
      <Button
        onClick={handleRun}
        disabled={!isReady || !hasCredits}
        className="w-full"
        size="lg"
      >
        {isRunning ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {isScreening ? 'Screening...' : 'Evaluating...'}
          </>
        ) : (
          <>
            <Play className="mr-2 h-4 w-4" />
            {isScreening
              ? `Screen All (${creditsNeeded} credit${creditsNeeded !== 1 ? 's' : ''})`
              : `Run Evaluation (${creditsNeeded} credit${creditsNeeded !== 1 ? 's' : ''})`}
          </>
        )}
      </Button>

      {/* Not enough credits warning */}
      {!creditsLoading && !hasCredits && !isRunning && (
        <p className="text-center text-xs text-rose-500">
          <AlertCircle className="mr-1 inline h-3 w-3" />
          Not enough credits. Purchase more on the Pricing page.
        </p>
      )}
    </div>
  );
}

// =========================================
// Sub-components
// =========================================

function ReadinessCheck({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {ok ? (
        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-slate-300" />
      )}
      <span className={cn('text-slate-600', !ok && 'text-slate-400')}>
        {label}
      </span>
      <span className="text-slate-400">— {detail}</span>
    </div>
  );
}
